import type { Connector } from "wagmi"

import type {
  FeaturedWallet,
  WalletAdapter,
  WalletConnectResult,
  WalletTransactionRequest,
} from "../types"
import {
  isWalletInstalled,
  resolveEvmConnectorForWallet,
} from "../detection"

export type WagmiConnectFn = (params: {
  connector: Connector
  chainId?: number
}) => Promise<{ accounts: readonly `0x${string}`[] }>

export type WagmiDisconnectFn = () => void

export type WagmiGetAccountFn = () => `0x${string}` | undefined

export type WagmiSignMessageFn = (params: {
  message: string
  account?: `0x${string}`
}) => Promise<`0x${string}`>

export type WagmiSendTransactionFn = (params: {
  to: `0x${string}`
  value?: bigint
  data?: `0x${string}`
  chainId?: number
}) => Promise<`0x${string}`>

export function resolveEvmConnector(
  wallet: FeaturedWallet,
  connectors: readonly Connector[],
): Connector | undefined {
  return resolveEvmConnectorForWallet(wallet, connectors)
}

export function resolveWalletConnectConnector(
  connectors: readonly Connector[],
): Connector | undefined {
  return connectors.find((connector) => connector.id === "walletConnect")
}

export class EvmWalletAdapter implements WalletAdapter {
  readonly id: string
  readonly name: string
  readonly chain = "evm" as const

  constructor(
    private readonly wallet: FeaturedWallet,
    private readonly connectors: readonly Connector[],
    private readonly connectAsync: WagmiConnectFn,
    private readonly disconnectFn: WagmiDisconnectFn,
    private readonly getAccount: WagmiGetAccountFn,
    private readonly signMessageFn?: WagmiSignMessageFn,
    private readonly sendTransactionFn?: WagmiSendTransactionFn,
    private readonly preferredChainId?: number,
  ) {
    this.id = wallet.id
    this.name = wallet.name
  }

  isInstalled(): boolean {
    return isWalletInstalled(this.wallet, this.connectors)
  }

  async connect(): Promise<WalletConnectResult> {
    const connector = resolveEvmConnectorForWallet(this.wallet, this.connectors)

    if (!connector) {
      // Do not silently fall back to WalletConnect for branded tiles —
      // that produces invalid/"null" address errors inside Tron DApp browsers.
      throw new Error(`${this.name} connector not found`)
    }

    const result = await this.connectAsync({
      connector,
      chainId: this.preferredChainId,
    })
    const address = result.accounts[0] ?? this.getAccount()
    if (!address || !/^0x[a-fA-F0-9]{40}$/.test(address)) {
      throw new Error("EVM wallet did not return an address")
    }
    return { address, chain: "evm" }
  }

  async disconnect(): Promise<void> {
    this.disconnectFn()
  }

  async getAddress(): Promise<string | null> {
    return this.getAccount() ?? null
  }

  async signMessage(message: string): Promise<string> {
    if (!this.signMessageFn) {
      throw new Error("Message signing not configured")
    }
    const account = this.getAccount()
    return this.signMessageFn({ message, account })
  }

  async sendTransaction(request: WalletTransactionRequest): Promise<string> {
    if (!this.sendTransactionFn) {
      throw new Error("Transactions not configured")
    }
    return this.sendTransactionFn({
      to: request.to as `0x${string}`,
      value:
        request.value === undefined
          ? undefined
          : typeof request.value === "bigint"
            ? request.value
            : BigInt(request.value),
      data: request.data,
      chainId: request.chainId,
    })
  }
}

export function createEvmAdapter(
  wallet: FeaturedWallet,
  deps: {
    connectors: readonly Connector[]
    connectAsync: WagmiConnectFn
    disconnect: WagmiDisconnectFn
    getAccount: WagmiGetAccountFn
    signMessage?: WagmiSignMessageFn
    sendTransaction?: WagmiSendTransactionFn
    chainId?: number
  },
): EvmWalletAdapter {
  return new EvmWalletAdapter(
    wallet,
    deps.connectors,
    deps.connectAsync,
    deps.disconnect,
    deps.getAccount,
    deps.signMessage,
    deps.sendTransaction,
    deps.chainId,
  )
}
